import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { QrCode, UserCheck, Eye, TrendingUp, Calendar, Building2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from 'recharts';
import { API } from '../App';

const StatTile = ({ label, value, sub, icon: Icon, color, testid }) => (
  <div className="bg-white border-2 border-charcoal rounded-xl p-3 flex items-start gap-3 shadow-brutal-sm" data-testid={testid}>
    <div className={`w-10 h-10 rounded-lg border-2 border-charcoal flex items-center justify-center shrink-0 ${color}`}>
      <Icon className="w-5 h-5 text-charcoal" />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wider text-charcoal/50 truncate">{label}</p>
      <p className="font-heading font-bold text-xl text-charcoal leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-charcoal/60 truncate">{sub}</p>}
    </div>
  </div>
);

const formatShortDate = (iso) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const OrgStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/organizations/my/stats`);
        if (alive) setStats(res.data);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="mb-6 h-32 bg-charcoal/5 rounded-xl animate-pulse" data-testid="stats-loading" />
    );
  }
  if (!stats || stats.orgs_count === 0) return null;

  const chartData = (stats.scans_timeseries || []).map((p) => ({ ...p, label: formatShortDate(p.date) }));
  const peak = Math.max(0, ...chartData.map((p) => p.scans));

  return (
    <section className="mb-6" data-testid="org-stats-panel">
      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
        <StatTile
          label="PingMes generes"
          value={stats.total_pingmes.toLocaleString()}
          sub={stats.total_quota > 0 ? `sur ${stats.total_quota.toLocaleString()} max` : null}
          icon={QrCode}
          color="bg-primary/20"
          testid="stat-total-pingmes"
        />
        <StatTile
          label="Taux d'enrolement"
          value={`${stats.enrollment_rate}%`}
          sub={`${stats.enrolled_pingmes.toLocaleString()} enroles`}
          icon={UserCheck}
          color="bg-green-200"
          testid="stat-enrollment-rate"
        />
        <StatTile
          label="Scans ce mois"
          value={stats.scans_this_month.toLocaleString()}
          sub={`${stats.total_scans.toLocaleString()} total`}
          icon={Calendar}
          color="bg-accent/30"
          testid="stat-scans-month"
        />
        <StatTile
          label="Organisations"
          value={stats.orgs_count.toLocaleString()}
          sub={stats.per_org.length > 0 ? stats.per_org[0].display_name : null}
          icon={Building2}
          color="bg-charcoal/10"
          testid="stat-orgs-count"
        />
      </div>

      {/* Chart */}
      <div className="bg-white border-2 border-charcoal rounded-xl p-4 shadow-brutal-sm" data-testid="stats-chart-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading font-bold text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Scans des 30 derniers jours
            </h3>
            <p className="text-[11px] text-charcoal/60">Evolution des scans publics de vos PingMes brandes.</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase font-bold text-charcoal/50 tracking-wider">Pic journalier</p>
            <p className="font-heading font-bold text-lg">{peak.toLocaleString()}</p>
          </div>
        </div>

        <div className="h-48 min-w-0 w-full" data-testid="stats-chart" style={{ minWidth: 0 }}>
          {chartData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="scans-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6B35" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#FF6B35" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} stroke="#0f172a" />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} stroke="#0f172a" />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '2px solid #0f172a', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ fontWeight: 700 }}
                  formatter={(v) => [v, 'Scans']}
                />
                <Area type="monotone" dataKey="scans" stroke="#FF6B35" strokeWidth={2} fill="url(#scans-grad)" />
                <Line type="monotone" dataKey="scans" stroke="#FF6B35" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Per-org breakdown */}
        {stats.per_org.length > 1 && (
          <div className="mt-4 pt-3 border-t border-charcoal/10">
            <p className="text-[10px] uppercase font-bold tracking-wider text-charcoal/50 mb-2">Repartition par organisation</p>
            <ul className="space-y-1.5">
              {stats.per_org
                .slice()
                .sort((a, b) => b.scans - a.scans)
                .map((o) => {
                  const rate = o.pingme_count ? Math.round((o.enrolled / o.pingme_count) * 100) : 0;
                  return (
                    <li key={o.org_id} className="flex items-center gap-2 text-xs" data-testid={`per-org-row-${o.org_id}`}>
                      <span
                        className="inline-block w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: o.primary_color || '#FF6B35' }}
                      />
                      <span className="font-bold truncate flex-1">{o.display_name || o.name}</span>
                      <span className="text-charcoal/60">{o.pingme_count} PingMes</span>
                      <span className="text-charcoal/60 hidden sm:inline">· {rate}% enroles</span>
                      <span className="font-bold text-charcoal">{o.scans} scans</span>
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export default OrgStatsPanel;
